<template>
  <div class="sidebar position-relative">
    <!-- <OrderFormUploadData
      :order="undefined"
      :title="$t('components.order-form.dataUploadTitle')"
      :fields="uploadDataFields"
      :isVisible="false"
      @progress="handleUploadProgress"
    /> -->
    <!-- Sidebar Items -->
    <div class="sidebar-item mb-4" v-for="item in sidebarItems" :key="item.label" @click="handlePreviousStepWhileUploading(item)">
      <div v-if="item.show === true" class="d-flex flex-column justify-content-center align-items-center">
        <div class="icon-container" :class="{active: item.route === isActiveMenu}">
          <img class="mb-2" :src="item.route === isActiveMenu ? activeIcons[item.route as SidebarRoute] : item.src" :alt="item.label" />
        </div>
        <p class="m-1 fw-normal fs-s text-wrapper">{{ item.label }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import i18n from '@/i18n';
import { useUserStore } from '@/stores/userStore';
import { Role } from '@/types/enum/user';
import { Vue, Component, Watch } from 'vue-facing-decorator';
import OrderFormUploadData from '@/components/forms/OrderFormUploadData.vue';
import { reactive, computed, watch } from 'vue';
import { ElMessageBox } from 'element-plus';
import { useUploadStore } from '@/stores/uploadStore';
import formUploadDataInitial from '@/data/formUploadDataInitial.json';
import { useRoute, useRouter } from 'vue-router';
import homeIcon from '@/assets/icons/newUI/home.svg';
import categoriesIcon from '@/assets/icons/newUI/categories.svg';
import yourProjectsIcon from '@/assets/icons/newUI/your_projects.svg';
import adminDashboardIcon from '@/assets/icons/newUI/admin_dashboard.svg';
import uploadIcon from '@/assets/icons/newUI/upload.svg';
import templatesIcon from '@/assets/icons/newUI/layouts.svg';
import ordersIcon from '@/assets/icons/newUI/orders.svg';
import { useFeatureFlagStore } from '@/stores/featureFlagStore';
import homeActiveIcon from '@/assets/icons/newUI/active/home-active.svg';
import yourProjectActiveIcon from '@/assets/icons/newUI/active/your_projects-active.svg';
import templatesActiveIcon from '@/assets/icons/newUI/active/layouts-active.svg';
import uploadActiveIcon from '@/assets/icons/newUI/active/upload-active.svg';
import categoriesActiveIcon from '@/assets/icons/newUI/active/categories-active.svg';
import ordersActiveIcon from '@/assets/icons/newUI/active/orders-active.svg';

type SidebarRoute = 'home' | 'admin' | 'productTypes' | 'projects' | 'templates' | 'template-upload' | 'orders';

@Component({
  components: {
    OrderFormUploadData
  }
})

@Component
export default class Sidebar extends Vue {
  isUserSupplier = false;
  isUserMarkus = false;
  isUserAuthenticated = false;
  userStore = useUserStore();
  route = useRoute();
  router = useRouter();
  featureFlagStore = useFeatureFlagStore();
  activeIcons: Record<SidebarRoute, string> = {
    'home': homeActiveIcon,
    'admin': adminDashboardIcon,
    'productTypes': categoriesActiveIcon,
    'projects': yourProjectActiveIcon,
    'templates': templatesActiveIcon,
    'template-upload': uploadActiveIcon,
    'orders': ordersActiveIcon,
  }

  get sidebarItems() {
    return [
      { show: true, label: i18n.global.t('views.configurator.home'), src: homeIcon, route: 'home' },
      // { show: true, label: i18n.global.t('views.configurator.productTypes'), src: categoriesIcon, route: 'productTypes' },
      { show: this.isUserMarkus, label: i18n.global.t('views.configurator.adminDashboard'), src: adminDashboardIcon, route: 'admin' },
      { show: this.isUserAuthenticated, label: i18n.global.t('views.configurator.orders'), src: ordersIcon, route: 'orders' },
      { show: this.isUserSupplier, label: i18n.global.t('views.configurator.yourProjects'), src: yourProjectsIcon, route: 'projects' },
      { show: this.isUserSupplier, label: i18n.global.t('views.configurator.templates'), src: templatesIcon, route: 'templates' },
      { show: this.isUserSupplier, label: i18n.global.t('views.configurator.upload'), src: uploadIcon, route: 'template-upload' },
    ].filter((menu)=>{
      if(menu.route === 'projects'){
        return this.featureFlagStore.isProjectListEnabled()
      }
      if(menu.route === 'templates'){
        return this.featureFlagStore.isTemplateListEnabled()
      }
      if(menu.route === 'template-upload'){
        return this.featureFlagStore.isTemplateUploadEnabled()
      }
      return menu.show
    });
  }

  get isActiveMenu() {
    let route: SidebarRoute;
    const { name } = this.route;

    if (name === 'home' || name === 'configurator') {
      route = 'home'
    } else if (name === 'templates' || name === 'template-workflow') {
      route = 'templates'
    } else if (name === 'projects' || name === 'order-workflow') {
      route = 'projects'
    } else if (name === 'admin') {
      route = 'admin'
    } else if (name === 'orders') {
      route = 'orders'
    } else {
      route = name as SidebarRoute;
    }

    return route;
  }

  state = reactive({
    isLoadingUploadData: false
  });

  mounted() {
    const uploadStore = useUploadStore();
    const isUploading = computed(() => uploadStore.isUploading);
    watch(isUploading, (newVal) => {
      this.state.isLoadingUploadData = newVal;
    });
    this.onRolesChanged()
  }

  @Watch('userStore.roles')
  onRolesChanged(): void {
    this.isUserSupplier = this.userStore.roles.includes(Role.Supplier);
    this.isUserMarkus = this.userStore.user?.username == "cekoon-supplier";
    this.isUserAuthenticated = this.userStore.isAuthenticated;
  }

  handleUploadProgress(isUploading: boolean) {
    this.state.isLoadingUploadData = isUploading;
  }

  handlePreviousStepWhileUploading(item: { route: string }): void {
    if (this.state.isLoadingUploadData) {
      ElMessageBox.confirm(
        this.$t('components.order-form.goBackWhileLoadingMessage'),
        'info',
        {
          confirmButtonText: this.$t('components.mesh-loader.yes'),
          cancelButtonText: this.$t('components.mesh-loader.no'),
          type: 'warning'
        }
      )
        .then(() => {

          this.handleSidebarItemClick(item);
        })
        .catch((err) => {
          console.error(err);
        });
    } else {

      this.handleSidebarItemClick(item);
    }
  }

  handleSidebarItemClick(item: { show?: boolean; label?: string; src?: string; route: any; }): void {
    if (item.route === 'home') {
      this.goHome();
      return;
    }

    if (item.route === 'orders') {
      if (this.route.name === 'orders') {
        window.dispatchEvent(new CustomEvent('refresh-orders'));
      } else {
        this.router.push({ name: 'orders' });
      }
      return;
    }

    this.router.push({ name: item.route });
  }

  // We have standalone home page, therefore we redirect users to home, not configurator
  goHome(){
      const routeSupplier = Array.isArray(this.route.query.supplier)
      ? this.route.query.supplier[0]
      : this.route.query.supplier || '';
    const supplier = (routeSupplier || localStorage.getItem('supplier') || '').toLowerCase();
    this.router.push({ name: 'home', query: { supplier } });
  }

  goConfigurator(): void {
    // local storage
    const routeSupplier = Array.isArray(this.route.query.supplier)
      ? this.route.query.supplier[0]
      : this.route.query.supplier || '';
    const supplier = (routeSupplier || localStorage.getItem('supplier') || '').toLowerCase();

    if (supplier === 'luttermann' || supplier === 'gzpilz') {
      // supplier with mutiple category articles
      this.router.push({ name: 'productTypes', query: { supplier } });
    } else if (supplier === 'aidddo' || supplier === 'wjtortho') {
      // supplier with only one category article
      this.router.push({ name: 'configurator', query: { sp: '1', supplier, productTypeId: '1' } });
    } else {
      // supplier that only have one configurator page (no steps in sidebar and generate option)
      let productTypeId = '';
      const ptq = this.route.query.productTypeId;
      if (Array.isArray(ptq)) {
        productTypeId = ptq[0] ?? '';
      } else {
        productTypeId = ptq ?? '';
      }
      this.router.push({ name: 'configurator', query: { sp: '1', supplier, productTypeId } });
    }
  }


  get uploadDataFields() {
    const addParams = [{ group: 'string', id: 'number' }];

    return formUploadDataInitial.map((row, index) => {
      const methodName = row.action; // Get the function name from the JSON file
      const method = (this as any)[methodName]; // Search for functions in the component
      const params = row.params || addParams[index]; // Ambil parameter tambahan dari JSON

      if (typeof method !== 'function') {
        console.error(`Method "${methodName}" not found on the component.`);
        return { ...row, action: undefined }; // Set undefined if the function is not found
      }

      //  Wrap the function with a closure to pass additional parameters
      return {
        ...row,
        action: (event: Event) => method(event, { ...params }),
      };
    });
  }

}
</script>

<style>
.sidebar {
  position: fixed;
  height: 93vh;
  width: 5rem !important;
  background-color: #eaeaea;
  padding: 1rem;
  padding-top: 2rem;
  box-shadow: 1px 0 10px #b1b1b1;
}

.sidebar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-align: center;
}

.text-wrapper {
  max-width: 4rem;
  font-size: 0.75rem;
  text-align: center;
  line-height: 1.2;
}

.icon-container.active {
  background-color: #d8d8d8 !important;
}
.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 7px;
  height: 40px;
  width: 40px;
  border-radius: 6px;
  background-color: transparent;
}
</style>
